// The column widths of a hosted table (DataTablePage.jsx), chosen by the person by dragging or
// with the keyboard on a column header's edge, and remembered in THIS browser per project and
// table under `unio.colwidths.{projectId}.{table}` (task 2026-10-21 "a hosted table's cells never
// hide their buttons, and its columns can be resized").
//
// Browser storage is a convenience only: it can be blocked (a private window, site data
// refused) and every accessor may throw, so each read and write is wrapped and a failure only
// means the widths are not remembered. Widths of columns the table no longer has (after a
// migration to a new design version) are dropped when the table is opened.
import { useCallback, useLayoutEffect, useRef, useState } from "react";

export const MIN_WIDTH = 80;        // px; no maximum
export const DEFAULT_WIDTH = 160;   // px; a column with no width yet (nothing measured, a new column)

export function widthsKey(projectId, table) {
  return `unio.colwidths.${projectId}.${table}`;
}

export function clampWidth(w) {
  const n = Math.round(Number(w));
  return Number.isFinite(n) ? Math.max(MIN_WIDTH, n) : MIN_WIDTH;
}

/** Only the columns the table has, each a finite width of at least MIN_WIDTH. */
export function pruneWidths(widths, columns) {
  const out = {};
  if (!widths || typeof widths !== "object" || Array.isArray(widths)) return out;
  (columns || []).forEach(c => {
    const w = Number(widths[c]);
    if (Object.prototype.hasOwnProperty.call(widths, c) && Number.isFinite(w)) out[c] = clampWidth(w);
  });
  return out;
}

function defaultStorage() {
  try {
    return typeof window !== "undefined" ? window.localStorage : null;
  } catch {
    return null;      // the accessor itself throws where site data is blocked
  }
}

/** The stored widths of `key`, pruned to `columns`; {} when nothing (or nothing readable) is stored. */
export function readWidths(key, columns, storage = defaultStorage()) {
  try {
    const raw = storage ? storage.getItem(key) : null;
    return raw ? pruneWidths(JSON.parse(raw), columns) : {};
  } catch {
    return {};
  }
}

/** Store `widths` under `key` (none: the entry is removed). False when the storage refused. */
export function writeWidths(key, widths, storage = defaultStorage()) {
  try {
    if (!storage) return false;
    if (widths && Object.keys(widths).length) storage.setItem(key, JSON.stringify(widths));
    else storage.removeItem(key);
    return true;
  } catch {
    return false;
  }
}

function sameWidths(a, b) {
  const ka = Object.keys(a);
  return ka.length === Object.keys(b).length && ka.every(k => a[k] === b[k]);
}

/**
 * useColumnWidths(key, columns) -> {widths, fixed, setAll, setWidth, reset}
 *   widths  {column: px} of the columns that have one ({} until the person resizes one)
 *   fixed   true once any width is set (the table then uses a fixed layout)
 *   setAll(widths), setWidth(column, px), reset()  — each stored at once
 * `columns` is null while the table is loading; nothing is read until it is known.
 */
export default function useColumnWidths(key, columns) {
  const names = columns ? columns.join("\u0000") : null;
  const [widths, setWidths] = useState({});
  const current = useRef({});       // the latest widths, so two calls in one event compose
  const cols = useRef([]);

  useLayoutEffect(() => {
    if (names === null) return;
    const list = names ? names.split("\u0000") : [];
    cols.current = list;
    let stored = {};
    let raw = null;
    try {
      const s = defaultStorage();
      raw = s ? s.getItem(key) : null;
      stored = raw ? JSON.parse(raw) : {};
    } catch {
      stored = {};
    }
    const pruned = pruneWidths(stored, list);
    // a column the table no longer has: forget its width in the storage too
    if (raw && !sameWidths(pruned, stored && typeof stored === "object" ? stored : {})) writeWidths(key, pruned);
    current.current = pruned;
    setWidths(pruned);
  }, [key, names]);

  const commit = useCallback(next => {
    current.current = next;
    writeWidths(key, next);
    setWidths(next);
  }, [key]);

  const setAll = useCallback(all => commit(pruneWidths(all, cols.current)), [commit]);
  const setWidth = useCallback((column, w) => {
    if (!cols.current.includes(column)) return;
    commit({ ...current.current, [column]: clampWidth(w) });
  }, [commit]);
  const reset = useCallback(() => commit({}), [commit]);

  return { widths, fixed: Object.keys(widths).length > 0, setAll, setWidth, reset };
}
